/*!
 * Copyright (c) Microsoft Corporation and contributors. All rights reserved.
 * Licensed under the MIT License.
 */
/**
 * Helper class for lazy initialized values. Ensures the value is only generated once, and remain immutable.
 * @internal
 */
export class Lazy {
    /**
     * Instantiates an instance of Lazy<T>.
     * @param valueGenerator - The function that will generate the value when value is accessed the first time.
     */
    constructor(valueGenerator) {
        this.valueGenerator = valueGenerator;
        this._evaluated = false;
    }
    /**
     * Return true if the value as been generated, otherwise false.
     */
    get evaluated() {
        return this._evaluated;
    }
    /**
     * Get the value. If this is the first call the value will be generated.
     */
    get value() {
        if (!this._evaluated) {
            this._evaluated = true;
            this._value = this.valueGenerator();
        }
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        return this._value;
    }
}
/**
 * A lazy evaluated promise. The execute function is delayed until
 * the promise is used, e.g. await, then, catch ...
 * The execute function is only called once.
 * All calls are then proxied to the promise returned by the execute method.
 * @legacy
 * @alpha
 */
export class LazyPromise {
    // eslint-disable-next-line @typescript-eslint/class-literal-property-style
    get [Symbol.toStringTag]() {
        return "[object LazyPromise]";
    }
    constructor(execute) {
        this.execute = execute;
    }
    // eslint-disable-next-line unicorn/no-thenable
    async then(
    // eslint-disable-next-line @rushstack/no-new-null
    onfulfilled, 
    // TODO: Use `unknown` instead (API breaking)
    // eslint-disable-next-line @rushstack/no-new-null, @typescript-eslint/no-explicit-any
    onrejected) {
        // eslint-disable-next-line prefer-rest-params
        return this.getPromise().then(...arguments);
    }
    async catch(
    // TODO: Use `unknown` instead (API breaking)
    // eslint-disable-next-line @rushstack/no-new-null, @typescript-eslint/no-explicit-any
    onrejected) {
        // eslint-disable-next-line prefer-rest-params
        return this.getPromise().catch(...arguments);
    }
    // eslint-disable-next-line @rushstack/no-new-null
    async finally(onfinally) {
        // eslint-disable-next-line prefer-rest-params
        return this.getPromise().finally(...arguments);
    }
    async getPromise() {
        if (this.result === undefined) {
            this.result = this.execute();
        }
        return this.result;
    }
}
//# sourceMappingURL=lazy.js.map